/**
 * CalendarCreateMenu — "Create", the calendar header's one primary action, with its two items.
 *
 * The Practice calendar's Create (PM `components/blocks/calendar-toolbar.tsx`): the button renders only when one of the
 * two permissions is held, each item only for its own permission (`Manage_Patient_Appointments` add → New Appointment;
 * `Doctor_Calender` add → Block Availability), both fail-closed (default false). PM's items: the label on the left, the
 * icon on the right (`justify-between`), PM's copy. Look: `.cal-create-*` in styles/showcase/calendar.css.
 */
import { useState } from 'react';
import { Ban, Calendar } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverItem,
  AnchoredPopoverTrigger,
  Button,
} from '@scripsteam/scrips-signal-ds';

export interface CalendarCreateMenuProps {
  canCreateAppointment?: boolean;
  canBlockAvailability?: boolean;
  onCreateClick: () => void;
  /** Kept for the component; the PA calendar passes neither this nor `canBlockAvailability` (no block availability,
   *  supervisor ruling 21 Oct), so the menu holds New Appointment alone. */
  onCreateBlockClick?: () => void;
}

export function CalendarCreateMenu({
  canCreateAppointment = false,
  canBlockAvailability = false,
  onCreateClick,
  onCreateBlockClick,
}: CalendarCreateMenuProps) {
  const [open, setOpen] = useState(false);
  if (!canCreateAppointment && !canBlockAvailability) return null;
  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger render={<Button aria-haspopup="menu" aria-expanded={open} className="cal-create-trigger" />}>
        Create
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent align="end" side="bottom" className="cal-create-menu" role="menu">
        {canCreateAppointment ? (
          <AnchoredPopoverItem role="menuitem" onClick={run(onCreateClick)}>
            <span>New Appointment</span>
            <Calendar aria-hidden className="cal-menu-icon" />
          </AnchoredPopoverItem>
        ) : null}
        {canBlockAvailability && onCreateBlockClick ? (
          <AnchoredPopoverItem role="menuitem" onClick={run(onCreateBlockClick)}>
            <span>Block Availability</span>
            <Ban aria-hidden className="cal-menu-icon" />
          </AnchoredPopoverItem>
        ) : null}
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
